import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Content } from '@patternfly/react-core';
import type { InFlightDomainRegistration } from '../../../hooks/useInFlightDomainRegistrations';

interface PendingRegistrationsAlertProps {
  registrations: InFlightDomainRegistration[];
  className?: string;
}

/**
 * Paid registrations the registry has not confirmed yet, shown above the domain list. Those domains
 * are deliberately absent from the list until the registry confirms them, so this is the only place
 * a customer who just paid sees them. Each name links to its order page. Renders nothing when no
 * registration is in flight.
 */
const PendingRegistrationsAlert: React.FunctionComponent<PendingRegistrationsAlertProps> = ({
  registrations,
  className,
}) => {
  const entries = registrations.flatMap((registration) =>
    registration.names.map((name) => ({ orderId: registration.orderId, name })),
  );
  if (entries.length === 0) {
    return null;
  }

  return (
    <Alert
      variant="info"
      isInline
      title={entries.length === 1 ? 'A domain registration is in progress' : 'Domain registrations are in progress'}
      className={className}
    >
      <Content component="p">
        {entries.length === 1 ? 'This domain appears' : 'These domains appear'} in your domain list as soon as the
        registry confirms the registration, usually within minutes.
      </Content>
      <Content component="ul">
        {entries.map(({ orderId, name }) => (
          <Content component="li" key={`${orderId}-${name}`}>
            <Link to={`/domain-orders/${orderId}/return`}>{name}</Link> — awaiting registry confirmation
          </Content>
        ))}
      </Content>
    </Alert>
  );
};

export default PendingRegistrationsAlert;
